import React, { useEffect, useState } from 'react';
import { Table, Card, Input, Select, Space, message, Typography } from 'antd';
import type { TableProps } from 'antd';
import { SearchOutlined, FilterOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import ApiService from '../service/api-service';
import { EchoSession, GetEchoSessionsParams } from '../types/echo';

const { Option } = Select;
const { Title } = Typography;

const EchoSessions: React.FC = () => {
  const { echoId } = useParams<{ echoId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EchoSession[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<'createdAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchSessions = async (params: GetEchoSessionsParams = {}) => {
    if (!echoId) return;
    
    try {
      setLoading(true);
      const response = await ApiService.getEchoSessions(echoId, params);
      
      if (response.success) {
        setData(response.data.sessions);
        setPagination({
          current: response.data.pagination.currentPage,
          pageSize: response.data.pagination.limit,
          total: response.data.pagination.total
        });
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [echoId]);

  const handleTableChange: TableProps<EchoSession>['onChange'] = (paginationParams, _, sorter) => {
    const params: GetEchoSessionsParams = {
      page: paginationParams.current,
      limit: paginationParams.pageSize,
      search: searchText,
      sortBy,
      sortOrder
    };

    fetchSessions(params);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    fetchSessions({
      page: 1,
      limit: pagination.pageSize,
      search: value,
      sortBy,
      sortOrder
    });
  };

  const handleSortOrderChange = (value: 'asc' | 'desc') => {
    setSortOrder(value);
    fetchSessions({
      page: pagination.current,
      limit: pagination.pageSize,
      search: searchText,
      sortBy,
      sortOrder: value
    });
  };

  const handleViewReport = (sessionId: string) => {
    navigate(`/echo-report/${sessionId}`);
  };

  const columns = [
    {
      title: 'Participant',
      key: 'name',
      render: (_: any, record: EchoSession) => (
        <div>
          <div className="font-medium text-gray-800">
            {`${record.audience.firstName} ${record.audience.lastName}`}
          </div>
          <div className="text-sm text-gray-500">{record.audience.email}</div>
        </div>
      )
    },
    {
      title: 'Interview Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <span className="text-gray-600">
          {dayjs(date).format('MMM D, YYYY')}
        </span>
      )
    }
  ];

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeftOutlined className="mr-2" /> Back to Echos
        </button>
        <Title level={4} className="text-gray-800 mb-2">Interview Sessions</Title>
        <p className="text-gray-500">View all interview sessions and their reports</p>
      </div>

      <Card className="shadow-sm rounded-lg border-gray-100">
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <Input
            placeholder="Search by name or email..."
            prefix={<SearchOutlined className="text-gray-400" />}
            className="max-w-xs !rounded-lg"
            allowClear
            onChange={(e) => handleSearch(e.target.value)}
          />
          <Space className="ml-auto">
            <span className="text-gray-500 flex items-center gap-2">
              <FilterOutlined /> Sort by:
            </span>
            <Select
              value={sortOrder}
              onChange={handleSortOrderChange}
              className="w-32 !rounded-lg"
              popupClassName="rounded-lg"
            >
              <Option value="asc">Oldest first</Option>
              <Option value="desc">Newest first</Option>
            </Select>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            ...pagination,
            className: "!rounded-lg",
            showTotal: (total) => `Total ${total} sessions`
          }}
          onChange={handleTableChange}
          loading={loading}
          onRow={(record) => ({
            onClick: () => handleViewReport(record.id),
            className: 'cursor-pointer hover:bg-gray-50/80 transition-colors duration-150'
          })}
          className="[&_.ant-table-thead_.ant-table-cell]:bg-gray-50 [&_.ant-table-thead_.ant-table-cell]:font-medium [&_.ant-table]:rounded-lg"
        />
      </Card>
    </div>
  );
};

export default EchoSessions; 